/**
 * Constantes e enums de domínio compartilhados entre API e web.
 * Os arrays `as const` servem tanto para `z.enum(...)` quanto para iteração na UI.
 */

export const VEHICLE_STATUS = ['ATIVO', 'EM_SERVICO', 'GUARDADO'] as const;
export type VehicleStatus = (typeof VEHICLE_STATUS)[number];

export const FEATURE_TYPE = [
  'PPF',
  'SEGURO',
  'IPVA',
  'LICENCIAMENTO',
  'GARANTIA',
  'REVISAO',
  'CUSTOM',
] as const;
export type FeatureType = (typeof FEATURE_TYPE)[number];

export const FEATURE_STATUS = ['ATIVO', 'VENCENDO', 'VENCIDO'] as const;
export type FeatureStatus = (typeof FEATURE_STATUS)[number];

export const SERVICE_ORDER_STATUS = [
  'ABERTA',
  'EM_ANDAMENTO',
  'AGUARDANDO_PECA',
  'PAUSADA',
  'CONCLUIDA',
  'CANCELADA',
] as const;
export type ServiceOrderStatus = (typeof SERVICE_ORDER_STATUS)[number];

/** Status que contam como "em pátio / parado" para métricas e kiosk. */
export const OPEN_OS_STATUS: ServiceOrderStatus[] = [
  'ABERTA',
  'EM_ANDAMENTO',
  'AGUARDANDO_PECA',
  'PAUSADA',
];

export const PRIORITY = ['BAIXA', 'MEDIA', 'ALTA', 'URGENTE'] as const;
export type Priority = (typeof PRIORITY)[number];

/** Ciclo de vida de uma peça solicitada/comprada para uma OS. */
export const PART_REQUEST_STATUS = [
  'SOLICITADA',
  'PEDIDA',
  'EM_TRANSITO',
  'ENTREGUE',
  'CANCELADA',
] as const;
export type PartRequestStatus = (typeof PART_REQUEST_STATUS)[number];

/** Status de peça solicitada que conta como "ainda não chegou" (OS aguardando). */
export const PENDING_PART_REQUEST_STATUS: PartRequestStatus[] = [
  'SOLICITADA',
  'PEDIDA',
  'EM_TRANSITO',
];

/** Transportadoras usadas nas importações/compras de peças. */
export const SHIPPING_CARRIERS = ['FedEx', 'DHL', 'UPS', 'Correios'] as const;
export type ShippingCarrier = (typeof SHIPPING_CARRIERS)[number];

/** Moedas suportadas para o valor do produto (importações + nacional). */
export const CURRENCY = ['BRL', 'USD', 'EUR', 'GBP', 'JPY'] as const;
export type Currency = (typeof CURRENCY)[number];

/** Tipos de documento anexável a uma peça solicitada. */
export const PART_DOC_TYPE = ['INVOICE', 'NOTA_FISCAL', 'OUTRO'] as const;
export type PartDocType = (typeof PART_DOC_TYPE)[number];

/** Origem de uma entrada do log de uma peça (de onde veio a informação). */
export const PART_LOG_SOURCE = [
  'MANUAL',
  'EMAIL',
  'CORREIOS',
  'FEDEX',
  'UPS',
  'DHL',
  'INVOICE',
  'SISTEMA',
] as const;
export type PartLogSource = (typeof PART_LOG_SOURCE)[number];

export const PART_MOVEMENT_TYPE = ['ENTRADA', 'SAIDA', 'AJUSTE', 'RESERVA'] as const;
export type PartMovementType = (typeof PART_MOVEMENT_TYPE)[number];

/** Classificação de um item consumido numa OS: produto (consumível) ou peça. */
export const PART_USAGE_KIND = ['PRODUTO', 'PECA'] as const;
export type PartUsageKind = (typeof PART_USAGE_KIND)[number];

export const PREVENTIVE_INTERVAL_TYPE = ['TEMPO', 'KM', 'AMBOS'] as const;
export type PreventiveIntervalType = (typeof PREVENTIVE_INTERVAL_TYPE)[number];

export const PREVENTIVE_STATUS = ['OK', 'VENCENDO', 'VENCIDA'] as const;
export type PreventiveStatus = (typeof PREVENTIVE_STATUS)[number];

export const USER_ROLE = ['ADMIN', 'GERENTE', 'MECANICO', 'VISUALIZADOR'] as const;
export type UserRole = (typeof USER_ROLE)[number];

/** Grupos do Cognito = papéis. */
export const COGNITO_GROUPS = USER_ROLE;

/** Nomes dos índices da tabela single-table. */
export const INDEX = {
  GSI1: 'GSI1',
  GSI2: 'GSI2',
  GSI3: 'GSI3',
} as const;

/** Discriminador de entidade gravado em cada item (atributo `type`). */
export const ENTITY = {
  VEHICLE: 'Vehicle',
  FEATURE: 'VehicleFeature',
  SERVICE_ORDER: 'ServiceOrder',
  PART: 'Part',
  PART_MOVEMENT: 'PartMovement',
  PREVENTIVE: 'PreventiveMaintenance',
  USER: 'User',
} as const;
export type EntityType = (typeof ENTITY)[keyof typeof ENTITY];

/** Default de dias-antes para alerta de vencimento, quando não informado. */
export const DEFAULT_LEMBRETE_DIAS_ANTES = 30;

/** Modelo Claude vigente (configurável via env). */
export const DEFAULT_CLAUDE_MODEL = 'claude-sonnet-4-6';
